import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import Loader from "src/components/common/Loader";
import ProgramDocumentCard from "src/components/onboarding/program-documents/ProgramDocumentCard";

import supportPrograms from "src/data/app/programs";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as actions from "src/redux/slices/programSlice";
import {
  getMilestoneDocCopy,
  matchRequiredDocConfig,
  normalizeDocumentStatus,
  parsePeriod,
} from "src/constants/milestones";
import { Milestone, ProgramRecordType } from "src/types/program.types";

const DEFAULT_FILE_TYPES = ["pdf", "jpg", "png"];
const DEFAULT_MAX_FILE_SIZE_IN_MB = 10;

// The API only tells us the label of each required doc ("Purchase Invoice"),
// so pair it up with the program config entry that carries the upload rules
// (document_type, supported types, size cap) and fall back to a sane default
// when the config has no matching entry.
const buildDocumentData = (
  requiredDocLabel: string,
  configDocuments: any[],
) => {
  const configDocument = matchRequiredDocConfig(
    requiredDocLabel,
    configDocuments,
  );

  if (configDocument) return configDocument;

  return {
    id: requiredDocLabel,
    title: requiredDocLabel,
    description: `Upload your ${requiredDocLabel.toLowerCase()}`,
    required: true,
    supported_file_types: DEFAULT_FILE_TYPES,
    max_file_size_in_mb: DEFAULT_MAX_FILE_SIZE_IN_MB,
    document_type: requiredDocLabel,
  };
};

// The card content comes from the records API — it's the one that knows the
// file, the ids needed to re-upload and the live review status. The milestone's
// own `documents` are only used to decide *which* records belong here, since
// their status lags behind (an uploaded doc can still read "Upload Pending").
//
// A record qualifies when the milestone lists its id, or when it was uploaded
// against this milestone (record_meta_info.milestone). Records from other
// milestones with the same title are left out.
const resolveUploadedRecord = (
  documentType: string,
  milestone: Milestone,
  milestoneKey: string | undefined,
  programRecords: ProgramRecordType[],
): ProgramRecordType | null => {
  const milestoneDocuments = milestone.documents || [];
  const candidates = programRecords.filter(
    (programRecord) => programRecord?.title === documentType,
  );

  const record =
    candidates.find((programRecord) =>
      milestoneDocuments.some(
        (milestoneDocument) =>
          milestoneDocument.patientRecordsId &&
          milestoneDocument.patientRecordsId ===
            programRecord?.patient_records_id,
      ),
    ) ||
    candidates.find(
      (programRecord) =>
        programRecord?.record_meta_info?.milestone === milestoneKey,
    );

  if (!record) return null;

  // The rejection reason can sit on either side; the record wins when it has
  // one, otherwise fall back to what the milestone reported.
  const milestoneDocument = milestoneDocuments.find(
    (item) => item.patientRecordsId === record.patient_records_id,
  );

  return {
    ...record,
    record_meta_info: {
      ...record.record_meta_info,
      report_status: normalizeDocumentStatus(
        record.record_meta_info?.report_status,
      ),
      ...(record.record_meta_info?.rejected_reason ||
      !milestoneDocument?.rejectionReason
        ? {}
        : { rejected_reason: milestoneDocument.rejectionReason }),
    },
  } as ProgramRecordType;
};

const MilestoneDocumentsScreen: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const milestoneKey = params["milestone-key"];
  const dispatch = useAppDispatch();

  const { patient } = useAppSelector((state) => state.auth);
  const { programDetails, programRecords } = useAppSelector(
    (state) => state.program,
  );

  const config =
    supportPrograms[supportProgramKey as keyof typeof supportPrograms];
  const documentType = config?.documents?.document_type;
  const configDocuments = useMemo(
    () => config?.documents?.required_documents || [],
    [config],
  );

  const milestone: Milestone | undefined =
    programDetails.data?.milestones?.find((item) => item.key === milestoneKey);

  useEffect(() => {
    // Event: UPLOAD_DOCUMENTS_VIEWED
    analytics.pageView(SCREEN_NAMES.UPLOAD_DOCUMENTS_SCREEN);
  }, []);

  useEffect(() => {
    getProgramRecords();
    // Program details may not be in the store yet (deep link / refresh), and
    // even when they are they go stale as soon as a document is uploaded.
    fetchProgramDetails();
  }, [dispatch]);

  const fetchProgramDetails = () => {
    new Promise((resolve, reject) => {
      dispatch(actions.getProgramDetailsGfcRequest({ resolve, reject }));
    }).catch((error) => {
      console.error("Error while fetching GFC program details", error);
    });
  };

  const getProgramRecords = () => {
    const payload = {
      patient_id: patient?.data?.patient_id,
      report_type: documentType,
      // Scopes the records to this milestone — the same key the upload is
      // tagged with.
      milestone: milestoneKey,
    };
    new Promise((resolve, reject) => {
      dispatch(actions.getProgramRecordsRequest({ resolve, reject, payload }));
    }).catch((error) => {
      console.error("Error while fetching program records", error);
    });
  };

  // One card per required doc, filled in from the records API.
  const documents = useMemo(() => {
    if (!milestone) return [];

    const records = programRecords?.data || [];

    return (milestone.requiredDocs || []).map((requiredDocLabel) => {
      const documentData: any = buildDocumentData(
        requiredDocLabel,
        configDocuments as any[],
      );

      return {
        ...documentData,
        uploadedDocument: resolveUploadedRecord(
          documentData?.document_type,
          milestone,
          milestoneKey,
          records,
        ),
      };
    });
  }, [milestone, milestoneKey, programRecords?.data, configDocuments]);

  const isLoading =
    (programDetails.isLoading && !programDetails.data) ||
    (programRecords.isLoading && !programRecords.data);

  const { label: periodLabel, value: periodValue } = parsePeriod(
    milestone?.period || "",
  );

  return (
    <Layout
      supportProgramKey={supportProgramKey || ""}
      header={
        <Header
          title="Program Documents"
          titlePosition="center"
          showBackButton={true}
          onBackClick={() => navigate(-1)}
        />
      }
      stickyHeader={true}
      showBottomBar={false}
    >
      <div className="p-4 h-full">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-2 h-full">
            <Loader />
            <p className="text-sm text-gray-500">Fetching Documents...</p>
          </div>
        ) : !milestone ? (
          <p className="text-sm text-gray-500">Milestone not found.</p>
        ) : documents.length === 0 ? (
          <p className="text-sm text-gray-500">
            No documents are required for this milestone.
          </p>
        ) : (
          <>
            <div className="my-2">
              <h1
                className="text-lg font-bold"
                style={{ color: "var(--text-primary)" }}
              >
                {periodValue ? `${periodLabel} ${periodValue}` : periodLabel}{" "}
                Documents
              </h1>
              <p className="text-[var(--text-secondary)] mt-1">
                {getMilestoneDocCopy(milestone).description}
              </p>
            </div>

            <div className="flex flex-col gap-4 mt-4">
              {documents.map((document) => (
                <ProgramDocumentCard
                  key={document?.id}
                  className="bg-white"
                  docCardClassName="bg-gray-50"
                  documentData={document}
                  documentType={documentType}
                  document={document?.uploadedDocument}
                  milestone={milestone.key}
                  onSuccess={() => {
                    // Event: RECORD_UPLOADED_SUCCESSFULLY
                    analytics.trackEvent(EVENT_NAMES.RECORD_UPLOAD_SUCCESS, {
                      screen_name: SCREEN_NAMES.UPLOAD_DOCUMENTS_SCREEN,
                      support_program_key: supportProgramKey,
                      document_type: document?.document_type,
                      milestone_key: milestone.key,
                    });

                    getProgramRecords();
                    fetchProgramDetails();
                  }}
                  onError={() => {
                    // Event: RECORD_UPLOAD_ERROR
                    analytics.trackEvent(EVENT_NAMES.RECORD_UPLOAD_ERROR, {
                      screen_name: SCREEN_NAMES.UPLOAD_DOCUMENTS_SCREEN,
                      support_program_key: supportProgramKey,
                      document_type: document?.document_type,
                      milestone_key: milestone.key,
                    });
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default MilestoneDocumentsScreen;
